import React, { useMemo, useEffect, useState } from 'react';
import {
  shape, string, func, bool,
} from 'prop-types';
import _ from 'lodash';
import { Query } from 'react-apollo';
import { withTracking } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { LoadingIndicator } from 'components/common';
import Layout from 'components/layout';
import ProductPage, { staticTextKeys as productPageStaticTextKeys } from 'pages/Verticals/ProductPage/ProductPage';
import ResultsPage, { staticTextKeys as resultsPageStaticTextKeys } from 'pages/Verticals/ResultsPage/ResultsPage';
import {
  getFunnelQueryFromSearchParams,
  getIsEditingFunnelFromSearchParams,
  parseSearchParamsFilterValues,
} from 'pages/Verticals/utils';
import {
  verticalTrack,
} from 'pages/Verticals/ResultsPage/ResultsPageTrackingUtils';
import ErrorPage from 'pages/ErrorPage';
import Queries from 'queries';
import {
  getAuthQueryVariables, getStaticText, getImages,
} from 'helpers/Utils';

const isVerticalQuery = (
  searchParamsFilterValues, verticalEnum
) => _.isEmpty(searchParamsFilterValues)
  && (verticalEnum === 'CC' || verticalEnum === 'PL');

const isFunnelPageQuery = (funnelQuery, verticalEnum) => !funnelQuery
  && (verticalEnum === 'TI' || verticalEnum === 'HL');

const VerticalQuery = ({
  match,
  localisation,
  resultsPageConfig,
  vertical,
  language,
  location,
  track,
  topBanner,
  contentstackKeys,
  isResultsPageRoute,
}) => {
  const { params } = match;
  const { list } = params;
  const { enum: verticalEnum, defaultList } = resultsPageConfig;
  const [isQueryError, setIsQueryError] = useState(false);

  const isEditingFunnel = getIsEditingFunnelFromSearchParams(location.search);

  // The value will not change until vertical/languge change
  const initialSearchParamsFilterValues = useMemo(
    () => {
      const { returnObject } = parseSearchParamsFilterValues(location.search, setIsQueryError);
      return returnObject;
    },
    [verticalEnum, language]
  );

  // The value will not change until vertical/languge change
  const initialFunnelQuery = useMemo(
    () => getFunnelQueryFromSearchParams(location.search),
    [verticalEnum, language]
  );

  useEffect(() => {
    verticalTrack({ enum: verticalEnum, track });
  }, [verticalEnum, language, list]);

  // The value will not change until vertical/language change
  const initialList = useMemo(() => list, [verticalEnum, language]);

  const initialQuery = (() => {
    if (isQueryError) {
      return Queries.GetErrorPage;
    }
    if (isVerticalQuery(initialSearchParamsFilterValues, verticalEnum)) {
      return Queries.GetVerticalQuery;
    }
    if (isFunnelPageQuery(initialFunnelQuery, verticalEnum)) {
      return Queries[resultsPageConfig.funnelPageQuery];
    }
    return Queries[resultsPageConfig.query];
  })();
  const variables = {
    vertical: verticalEnum,
    language: localisation[language],
    ...getAuthQueryVariables({
      contentstackKeys,
      initailStaticTextKeys: [
        ...resultsPageStaticTextKeys,
        ...productPageStaticTextKeys,
      ],
      initialImages: [
        `${verticalEnum}_Results_Banner_Desktop`,
        `${verticalEnum}_Results_Banner_Mobile`,
      ],
      initialComponentConfig: contentstackKeys.componentConfig.resultsPage,
    }),
    list: initialList?.toLowerCase() ?? resultsPageConfig.defaultList?.toLowerCase(),
    promotionalBanners: [],
    ...initialSearchParamsFilterValues,
    ...initialFunnelQuery,
  };

  const {
    desktopImageKey: resultsDesktopImageKey,
    mobileImageKey: resultsMobileImageKey,
  } = resultsPageConfig?.topBanner || {};
  if (resultsDesktopImageKey && resultsMobileImageKey) {
    variables.promotionalBanners.push(resultsDesktopImageKey, resultsMobileImageKey);
  }

  const {
    desktopImageKey: funnelDesktopImageKey,
    mobileImageKey: funnelMobileImageKey,
  } = topBanner || {};
  if (funnelDesktopImageKey && funnelMobileImageKey) {
    variables.promotionalBanners.push(funnelDesktopImageKey, funnelMobileImageKey);
  }

  return (
    <Query
      query={initialQuery}
      variables={variables}
    >
      {({
        loading, data, client, refetch,
      }) => {
        const seoMetadata = data?.verticalQuery?.seoMetadata || data?.seoMetadata || {};
        const result = data?.verticalQuery?.result || data?.resultsPage || {};
        const componentConfig = data?.componentConfig || [];
        const pageConfig = data?.pageConfig || {};
        const allCountries = data?.allCountries || [];
        const { __typename: resultDataType } = result;
        const isProductPage = resultDataType === 'CreditCard';
        const isResultsPage = resultDataType
          && (resultDataType.includes('ResultsPage')
            || resultDataType.includes('ResultPage'));
        const isProviderResultsPage = isResultsPage && resultDataType.includes('Provider');
        const compositeUpdatedAt = data?.resultsPage?.compositeUpdatedAt || null;

        const verticalName = data?.resultsPage?.verticalName || null;
        // Only For TI funnel page in this stage
        const excludeStructuredData = verticalEnum !== 'TI' && defaultList !== 'funnel';
        // Exclude results page with search params from search engine robots
        const noIndex = !_.isEmpty(initialFunnelQuery)
          || !_.isEmpty(initialSearchParamsFilterValues);

        return (
          <Layout
            {...pageConfig}
            componentConfig={componentConfig}
            seoMetadata={
              {
                ...seoMetadata,
                noIndex,
                compositeUpdatedAt,
                verticalName,
                excludeStructuredData,
              }
            }
            customerProfile={data?.customerProfile}
            staticText={getStaticText(data)}
            images={getImages(data)}
            refetch={refetch}
            faqs={result?.faqs?.faqs}
            faqSeoIndexes={result?.faqSeoIndexes}
          >
            {((loading && <LoadingIndicator />)
              || (isProductPage && (
                <ProductPage
                  categories={data.relatedLists}
                  vertical={vertical}
                  product={result}
                  language={language}
                  locale={localisation[language]}
                  {...pageConfig}
                  seoTable={seoMetadata?.seoTable}
                />
              ))
              || (isResultsPage && isResultsPageRoute && (
                <ResultsPage
                  {...resultsPageConfig}
                  {...result}
                  {...pageConfig}
                  customerProfile={data?.customerProfile}
                  vertical={vertical}
                  language={localisation[language]}
                  displayLanguage={language}
                  relatedLists={data.relatedLists}
                  // don't pass list to results page when provider
                  list={isProviderResultsPage ? null : list}
                  initialFilterValues={initialSearchParamsFilterValues}
                  initialFunnelQuery={initialFunnelQuery}
                  isEditingFunnel={isEditingFunnel}
                  apolloClient={client}
                  promotionalBanners={data?.promotionalBanners}
                  providerImages={data?.providerImages}
                  seoTable={seoMetadata?.seoTable}
                  // For SG TI
                  allCountries={allCountries}
                />
              ))
              || <ErrorPage errorCode={404} />
            )}
          </Layout>
        );
      }}
    </Query>
  );
};

VerticalQuery.propTypes = {
  location: shape({}),
  match: shape({}),
  localisation: shape({}),
  resultsPageConfig: shape({}),
  vertical: string,
  language: string.isRequired,
  track: func,
  topBanner: shape({ desktopImageKey: string, mobileImageKey: string }),
  contentstackKeys: shape({}).isRequired,
  isResultsPageRoute: bool,
};

VerticalQuery.defaultProps = {
  location: {},
  match: {},
  localisation: {},
  resultsPageConfig: {},
  vertical: null,
  track: undefined,
  topBanner: {},
  isResultsPageRoute: true,
};

export default withTracking(withApplicationConfig(VerticalQuery, ['languages', 'ti-funnel', 'contentstackKeys']));
